<template>
  <div>
    <BubbleHeader :bubble="bubble" />
    <div class="p-6">
      <div
        v-if="!bubble.processed"
        class="mb-6 p-4 text-center text-gray-600 bg-yellow-100 rounded-lg"
      >
        <p v-if="!bubble.processed">
          This tweet is still being processed. Please check back later.
        </p>
        <p v-else-if="bubble.part">
          This tweet has been partially processed. Some information may be missing.
        </p>
      </div>
      <div v-if="bubble.author" class="mb-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <NuxtImg
              v-if="authorPicSrc"
              :src="authorPicSrc"
              alt="Author"
              class="w-10 h-10 rounded-full mr-3"
            />
            <div v-if="bubble.authorHandle">
              <a
                :href="`https://twitter.com/${bubble.authorHandle}`"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold hover:underline text-black"
              >
                {{ bubble.author[0] }}
              </a>
              <p class="text-sm text-gray-500">@{{ bubble.authorHandle }}</p>
            </div>
          </div>
          <div v-if="bubble.tweetCreatedAt">
            <p class="text-sm text-gray-700">
              {{ formatDate(bubble.tweetCreatedAt) }}
            </p>
          </div>
        </div>
      </div>
      <div class="max-w-3xl mx-auto">
        <div class="space-y-4 mb-6">
          <div v-if="bubble.tweetRootText" class="break-words">
            <TweetText :text="bubble.tweetRootText" />
          </div>
          <div v-if="hasRootMedia" class="relative w-full">
            <TweetMediaGrid
              :media-items="rootMediaItems"
              class="w-full"
              @video-play="onRootVideoPlay"
            />
          </div>
        </div>
        <div v-if="quote" class="mt-4 mb-6 border rounded-lg p-4 bg-muted/50">
          <div class="flex items-center mb-3">
            <NuxtImg
              v-if="quoteAuthorPicSrc"
              :src="quoteAuthorPicSrc"
              alt="Quote Author"
              class="w-8 h-8 rounded-full mr-2"
            />
            <div>
              <a
                :href="`https://twitter.com/${quote?.quote_author_handle}`"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold hover:underline text-black"
              >
                {{ quote?.quote_author?.[0] }}
              </a>
              <p class="text-sm text-gray-500">@{{ quote?.quote_author_handle }}</p>
            </div>
          </div>
          <div class="space-y-4">
            <div class="break-words">
              <TweetText :text="quote?.quote_full_text || ''" />
            </div>
            <div v-if="hasQuoteMedia" class="relative w-full">
              <TweetMediaGrid
                :media-items="quoteMediaItems"
                class="w-full"
                @video-play="onQuoteVideoPlay"
              />
            </div>
          </div>
        </div>
        <div v-if="threadTweets.length > 0" class="mt-6">
          <div class="flex items-center gap-2 mb-4">
            <div class="h-px bg-gray-200 flex-grow"></div>
            <h3 class="text-lg font-semibold text-gray-700">Thread</h3>
            <div class="h-px bg-gray-200 flex-grow"></div>
          </div>
          <div class="space-y-6">
            <div v-for="(tweet, index) in threadTweets" :key="index" class="relative">
              <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 -z-10"></div>
              <div class="pl-8 relative">
                <div
                  class="absolute left-[14px] top-2 w-3 h-3 bg-gray-300 rounded-full border-2 border-white"
                ></div>
                <div class="text-sm text-gray-500 mb-2">
                  {{ formatDate(tweet.tweet_id) }}
                </div>
                <div class="bg-white rounded-lg border p-4 space-y-4">
                  <div class="break-words">
                    <TweetText :text="tweet.tweet_full_text || ''" />
                  </div>
                  <div v-if="getThreadTweetMediaItems(tweet).length > 0" class="relative w-full">
                    <TweetMediaGrid
                      :media-items="getThreadTweetMediaItems(tweet)"
                      class="w-full"
                      @video-play="(index: number) => onThreadVideoPlay(index, index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TwitterBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import TweetText from '@/components/tweet/TweetText.vue'
import TweetMediaGrid from '@/components/tweet/TweetMediaGrid.vue'
import { useMediaItems } from '@/composables/useMediaItems'
import { useNavigationStore } from '@/stores/navigationStore'
import { useRouter } from 'vue-router'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

// Add interface for quote object since it's typed as Record<string, unknown>
interface Quote {
  quote_author: string[]
  quote_author_handle: string
  quote_author_pic?: string
  quote_full_text: string
}

const props = defineProps<{
  bubble: TwitterBubble
}>()

// Get media handling functions
const { getAuthorPicSrc, getQuoteAuthorPicSrc, getRootMediaItems, getQuoteMediaItems } =
  useMediaItems()

// Use the type navigation composable
useTypeNavigation(props.bubble)

// Computed properties for media sources
const authorPicSrc = computed(() => getAuthorPicSrc(props.bubble))
const quoteAuthorPicSrc = computed(() => getQuoteAuthorPicSrc(props.bubble))

const rootMediaItems = computed(() => getRootMediaItems(props.bubble))
const hasRootMedia = computed(() => rootMediaItems.value.length > 0)
const quoteMediaItems = computed(() => getQuoteMediaItems(props.bubble))
const hasQuoteMedia = computed(() => quoteMediaItems.value.length > 0)

// Video playing states
const rootVideoPlaying = ref<{ [key: number]: boolean }>({})
const isQuoteVideoPlaying = ref(false)
const threadVideoPlaying = ref<{ [key: string]: boolean }>({})

// Event handlers
function onRootVideoPlay(index: number) {
  rootVideoPlaying.value[index] = true
}

function onQuoteVideoPlay() {
  isQuoteVideoPlaying.value = true
}

function onThreadVideoPlay(tweetIndex: number, mediaIndex: number) {
  const key = `${tweetIndex}-${mediaIndex}`
  threadVideoPlaying.value[key] = true
}

// Thread handling with proper date field
const threadTweets = computed(() => {
  if (!props.bubble.thread || props.bubble.thread.length <= 1) return []
  return props.bubble.thread.slice(1)
})

// Update the quote access to use proper typing with type checking
const quote = computed(() => {
  if (!props.bubble.quote) return undefined
  const q = props.bubble.quote as Record<string, unknown>
  if (
    Array.isArray(q.quote_author) &&
    typeof q.quote_author_handle === 'string' &&
    typeof q.quote_full_text === 'string'
  ) {
    return {
      quote_author: q.quote_author,
      quote_author_handle: q.quote_author_handle,
      quote_author_pic: q.quote_author_pic as string | undefined,
      quote_full_text: q.quote_full_text,
    } as Quote
  }
  return undefined
})

// Date formatting
const formatDate = (dateString: string) => {
  if (!dateString) return ''

  // Try to parse the date and validate it
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    warn(`Invalid date value: ${dateString}`)
    return ''
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date)
  } catch (error) {
    logError(`Error formatting date: ${error}`)
    return ''
  }
}

const navigationStore = useNavigationStore()
const router = useRouter()

// Handle keyboard navigation
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') {
    const previousUuid = navigationStore.getPreviousItem(props.bubble.uuid)
    if (previousUuid) {
      router.push(`/bubble/${previousUuid}`)
    }
  } else if (event.key === 'ArrowRight') {
    const nextUuid = navigationStore.getNextItem(props.bubble.uuid)
    if (nextUuid) {
      router.push(`/bubble/${nextUuid}`)
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

// Add function to handle thread media items
function getThreadTweetMediaItems(tweet: {
  media_resources?: { [key: string]: string }
}): MediaItem[] {
  const items: MediaItem[] = []
  if (!tweet.media_resources) return items

  for (let i = 1; i <= 4; i++) {
    const videoKey = `video_${i}`
    const coverPhotoKey = `video_cover_photo_${i}`
    const imageKey = `image_${i}`
    const gifKey = `gif_${i}`
    const gifCoverPhotoKey = `gif_cover_photo_${i}`

    if (tweet.media_resources[gifKey]) {
      const gifSrc = tweet.media_resources[gifKey]
      const coverPhoto = tweet.media_resources[gifCoverPhotoKey]
      items.push({
        type: 'gif',
        src: gifSrc,
        cover_photo: coverPhoto,
      })
    } else if (tweet.media_resources[videoKey]) {
      const videoSrc = tweet.media_resources[videoKey]
      const coverPhoto = tweet.media_resources[coverPhotoKey]
      items.push({
        type: 'video',
        src: videoSrc,
        cover_photo: coverPhoto,
      })
    } else if (tweet.media_resources[imageKey]) {
      const imageSrc = tweet.media_resources[imageKey]
      items.push({
        type: 'image',
        src: imageSrc,
      })
    }
  }

  return items
}
</script>

<style scoped>
/* Scoped styles for TwitterDetailedView */
:deep(a) {
  @apply text-blue-500 hover:underline;
}
</style>
